import { useState, useEffect, Fragment } from 'react';
import { useNotify, useRedirect, Show, useRecordContext } from 'react-admin';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField,
  Autocomplete, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Divider, Alert, Tooltip,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon   from '@mui/icons-material/Save';
import CheckIcon  from '@mui/icons-material/Check';
import { useParams, useNavigate } from 'react-router-dom';
import authFetch from '../utils/authFetch';
import { card, btn, inputSx, tbl, NAVY, GOLD, fmtQty, sourceRowLabel, PRODUCTION_BATCH_STATUS, typo } from '../styles/ds';
import SupplierAllocationSelector from '../orders/SupplierAllocationSelector';

const CURRENCY = [{ id: 'ILS', label: '₪ שקל' }, { id: 'USD', label: '$ דולר' }];
let rk = 100;
const emptyComp = () => ({ _k: rk++, variant: null, quantity: 1, unit_cost_ils: '', unit_cost_usd: '', allocationValue: null, fulfillment: [], supplierPrices: null, supplierInventory: null });
const emptyCost = () => ({ _k: rk++, work_type: '', cost: '', currency: 'ILS', worker: null, notes: '' });

const fifoFromInventory = (inv) => {
  if (!inv?.length) return { fifoIls: '', fifoUsd: '' };
  const totalQty = inv.reduce((s, r) => s + Number(r.available_qty || 0), 0);
  if (totalQty === 0) return { fifoIls: '', fifoUsd: '' };
  const wIls = inv.reduce((s, r) => s + (Number(r.unit_cost_ils || 0)) * Number(r.available_qty || 0), 0);
  const wUsd = inv.reduce((s, r) => s + (Number(r.unit_cost_usd || 0)) * Number(r.available_qty || 0), 0);
  return { fifoIls: wIls > 0 ? (wIls / totalQty).toFixed(4) : '', fifoUsd: wUsd > 0 ? (wUsd / totalQty).toFixed(4) : '' };
};
const supplierCostPatch = (allocations, inv) => {
  const allocs = (allocations || []).filter(a => Number(a.requested_quantity) > 0);
  const totalQty = allocs.reduce((s, a) => s + Number(a.requested_quantity), 0);
  if (totalQty === 0 || !inv?.length) return {};
  let wIls = 0, wUsd = 0;
  for (const a of allocs) {
    const row = inv.find(p => Number(p.supplier_id) === Number(a.supplier_id));
    const qty = Number(a.requested_quantity);
    if (row) { wIls += (Number(row.unit_cost_ils) || 0) * qty; wUsd += (Number(row.unit_cost_usd) || 0) * qty; }
  }
  const patch = {};
  if (wIls > 0) patch.unit_cost_ils = (wIls / totalQty).toFixed(4);
  if (wUsd > 0) patch.unit_cost_usd = (wUsd / totalQty).toFixed(4);
  return patch;
};

function ComponentFulfillmentView({ fulfillment, unit }) {
  if (!fulfillment || fulfillment.length === 0) return null;
  return (
    <Box sx={{ mt: 0.5, mb: 1, p: 1, backgroundColor: 'rgba(212,237,218,0.25)',
      borderRadius: 1.5, border: '1px solid rgba(21,87,36,0.15)' }}>
      <Typography fontSize={11} fontWeight={700} color="#155724" mb={0.6}>סופק בפועל:</Typography>
      <Stack className="crm-wrap" direction="row" flexWrap="wrap" gap={0.8}>
        {fulfillment.map((g, i) => (
          <Chip key={i} size="small"
            label={`${sourceRowLabel(g)}: ${fmtQty(g.consumed_qty, unit)}`}
            sx={{ height: 20, fontSize: 10, fontWeight: 700, backgroundColor: '#D4EDDA', color: '#155724' }} />
        ))}
      </Stack>
    </Box>
  );
}

function ProductionShowContent() {
  const { id }   = useParams();
  const notify   = useNotify();
  const redirect = useRedirect();
  const navigate = useNavigate();

  const [batch,      setBatch]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [quantity,   setQuantity]   = useState(1);
  const [notes,      setNotes]      = useState('');
  const [components, setComponents] = useState([]);
  const [costs,      setCosts]      = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error,      setError]      = useState('');

  const [compVars,         setCompVars]         = useState([]);
  const [workers,          setWorkers]          = useState([]);
  const [roles,            setRoles]            = useState([]);
  const [productRoleCosts, setProductRoleCosts] = useState({});

  // Load batch
  useEffect(() => {
    authFetch(`/production_batches/${id}`)
      .then(r => r.json())
      .then(d => {
        setBatch(d);
        setQuantity(d.quantity || 1);
        setNotes(d.notes || '');
        const loadedComponents = (d.components || []).map(c => ({
          _k: rk++, _id: c.id,
          variant: c.component_variant_id
            ? { id: c.component_variant_id, label: c.component_name || `#${c.component_variant_id}`, quantity: c.current_stock, unit: c.unit }
            : null,
          quantity: Number(c.quantity) * (Number(d.quantity) || 1),
          unit_cost_ils: c.unit_cost_ils ?? '',
          unit_cost_usd: c.unit_cost_usd ?? '',
          allocationValue: c.allocation_mode === 'supplier_specific'
            ? { mode: 'supplier_specific', allocations: (c.allocations || []).map(a => ({
                source_type: a.source_type,
                supplier_id: a.supplier_id,
                supplier_name: a.supplier_name,
                requested_quantity: Number(a.requested_quantity) * (Number(d.quantity) || 1),
              }))}
            : { mode: 'automatic', allocations: [] },
          fulfillment: c.fulfillment || [],
          supplierInventory: null,
        }));
        setComponents(loadedComponents);
        // Fetch supplier inventory for all components so allocation changes can auto-fill costs
        const variantIds = [...new Set(loadedComponents.map(c => c.variant?.id).filter(Boolean))];
        if (variantIds.length > 0) {
          Promise.all(
            variantIds.map(vid =>
              authFetch(`/variants/${vid}/supplier-inventory`)
                .then(r => r.json()).then(d => ({ vid, rows: d.rows || [] }))
                .catch(() => ({ vid, rows: [] }))
            )
          ).then(results => {
            const invMap = new Map(results.map(r => [r.vid, r.rows]));
            setComponents(prev => prev.map(comp => ({
              ...comp,
              supplierInventory: comp.variant?.id ? (invMap.get(comp.variant.id) || []) : null,
            })));
          });
        }
        setCosts((d.costs || []).map(c => ({
          _k: rk++, _id: c.id,
          work_type: c.work_type || '',
          cost: c.cost || '',
          currency: c.currency || 'ILS',
          worker: c.worker_id ? { id: c.worker_id, label: c.worker_name || `#${c.worker_id}` } : null,
          notes: c.notes || '',
        })));
        setLoading(false);
      })
      .catch(() => setLoading(false));

    authFetch('/variants?_start=0&_end=500&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => {
        if (Array.isArray(d))
          setCompVars(d.filter(v => !v.product_is_finished_product)
            .map(v => ({ id: v.id, label: v.name || `#${v.id}`, quantity: v.quantity, price_ils: v.price_ils, price_usd: v.price_usd, unit: v.unit || 'יחידה' })));
      });
    authFetch('/workers?_start=0&_end=200&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => {
        if (Array.isArray(d)) setWorkers(d.map(w => ({ id: w.id, label: w.name, role_ids: w.role_ids || [] })));
      });
    authFetch('/worker_roles?_start=0&_end=100&_sort=name&_order=ASC')
      .then(r => r.json()).then(d => {
        if (Array.isArray(d)) setRoles(d);
      });
    authFetch('/product_role_costs')
      .then(r => r.json()).then(d => {
        if (d?.costs) setProductRoleCosts(d.costs);
      });
  }, [id]);

  const setComp = (k, p) => setComponents(cs => cs.map(c => c._k === k ? { ...c, ...p } : c));
  const setCost = (k, p) => setCosts(cs => cs.map(c => c._k === k ? { ...c, ...p } : c));

  const totalCompIls = components.reduce((s,c) => s + (Number(c.unit_cost_ils)||0)*(Number(c.quantity)||1), 0);
  const unitWorkIls  = costs.filter(c=>c.currency==='ILS').reduce((s,c)=>s+(Number(c.cost)||0),0);
  const totalWorkIls = unitWorkIls * (Number(quantity) || 1);
  const totalCostIls = totalCompIls + totalWorkIls;
  const unitCostIls  = quantity > 0 ? totalCostIls / quantity : 0;

  const isDraft = batch?.status === 'draft';

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        quantity: Number(quantity)||1, notes: notes||null,
        components: components.filter(c=>c.variant).map(c=>({
          variant_id: c.variant.id, quantity: (Number(c.quantity)||1) / (Number(quantity)||1),
          unit_cost_ils: Number(c.unit_cost_ils)||null,
          unit_cost_usd: Number(c.unit_cost_usd)||null,
          allocation_mode: c.allocationValue?.mode || 'automatic',
          allocations: c.allocationValue?.mode === 'supplier_specific'
            ? (c.allocationValue.allocations || [])
                .filter(a => a.source_type && Number(a.requested_quantity) > 0)
                .map(a => ({ ...a, requested_quantity: Number(a.requested_quantity) / (Number(quantity)||1) }))
            : [],
        })),
        costs: costs.filter(c=>c.cost&&Number(c.cost)>0).map(c=>({
          worker_id: c.worker?.id||null, work_type: c.work_type||null,
          cost: Number(c.cost), currency: c.currency, notes: c.notes||null,
        })),
      };
      const res = await authFetch(`/production_batches/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error||'שגיאה'); }
      notify('השינויים נשמרו', { type: 'success' });
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    if (!window.confirm(`לאשר ייצור מנה #${id}?\nהפעולה תנכה רכיבים מהמלאי ואינה הפיכה.`)) return;
    setConfirming(true); setError('');
    try {
      await handleSave(); // Save latest changes first
      const res = await authFetch(`/production_batches/${id}/confirm`, { method: 'POST' });
      if (!res.ok) { const b = await res.json().catch(()=>({})); throw new Error(b.error||'שגיאה'); }
      notify('הייצור אושר! המלאי עודכן', { type: 'success' });
      redirect('/production_batches');
    } catch(e) { setError(e.message); }
    finally { setConfirming(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  if (!batch)  return <Box p={4}><Typography color="error">מנה לא נמצאה</Typography></Box>;

  const st = PRODUCTION_BATCH_STATUS[batch.status] || { label: batch.status, bg: '#eee', color: '#555' };

  return (
    <Box className="crm-page crm-detail-content" p={3} sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography {...typo.pageTitle}>מנת ייצור #{id}</Typography>
          <Chip label={st.label} sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 700 }} />
        </Stack>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={btn.cancel}>חזרה</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Finished product ── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Typography fontWeight={700} color={NAVY} mb={1}>מוצר מוגמר</Typography>
          <Typography fontWeight={600} fontSize={16}>{batch.finished_name || `#${batch.finished_variant_id}`}</Typography>
          {isDraft ? (
            <TextField label="כמות לייצור" type="number" value={quantity}
              onChange={e => setQuantity(e.target.value.replace(/[^\d]/g, ''))}
              inputProps={{ min: 1, step: 1 }}
              sx={{ ...inputSx, width: 150, mt: 1.5 }} size="small" />
          ) : (
            <Typography mt={0.5} color="text.secondary">כמות: {batch.quantity}</Typography>
          )}
          {isDraft ? (
            <TextField label="הערות" value={notes} onChange={e => setNotes(e.target.value)}
              fullWidth multiline rows={2} sx={{ mt: 1.5, ...inputSx }} />
          ) : batch.notes && (
            <Typography mt={0.5} color="text.secondary" fontSize={13}>{batch.notes}</Typography>
          )}
          {batch.unit_cost_ils && (
            <Typography mt={1} fontSize={14} fontWeight={700} color="#856404">
              עלות קנייה ליחידה: ₪{Number(batch.unit_cost_ils).toLocaleString('he-IL',{minimumFractionDigits:2})}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Components ── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={700} color={NAVY}>רכיבים</Typography>
            {isDraft && <Button size="small" startIcon={<AddIcon />} onClick={() => setComponents(cs=>[...cs,{ ...emptyComp(), quantity: Number(quantity)||1 }])} sx={btn.primary}>הוסף</Button>}
          </Stack>
          <Box data-mobile-scroll sx={{ overflowX: 'auto' }}><Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 13, py: 1.2 } }}>
                <TableCell sx={{ minWidth: 220 }}>רכיב</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>כמות</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>
                  {isDraft
                    ? <Tooltip title="מחיר לפי אצוות המלאי — מתמלא אוטומטית" arrow placement="top"><span>עלות ₪ / יח&#39;</span></Tooltip>
                    : "עלות ₪ / יח'"}
                </TableCell>
                <TableCell align="center" sx={{ width: 110 }}>עלות $ / יח'</TableCell>
                {isDraft && <TableCell sx={{ width: 40 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map((c, i) => {
                const hasCost   = c.unit_cost_ils !== '' && Number(c.unit_cost_ils) > 0;
                const hasInvData = c.supplierInventory !== null;
                const noCostInv  = isDraft && hasInvData && !hasCost;
                const costTooltip = hasCost
                  ? 'מחיר חושב אוטומטית מאצוות מלאי — ניתן לשנות ידנית'
                  : noCostInv ? 'לא נמצא מחיר ברכישות — הזן ידנית' : '';
                return (
                <Fragment key={c._k}>
                <TableRow sx={tbl.zebraRow(i)}>
                  <TableCell sx={{ py: 0.8 }}>
                    {isDraft ? (
                      <>
                        <Autocomplete size="small" options={compVars} value={c.variant}
                          onChange={async (_, v) => {
                            if (!v) { setComp(c._k, { variant: null, unit_cost_ils: '', unit_cost_usd: '', allocationValue: null, supplierPrices: null, supplierInventory: null }); return; }
                            let supplierPrices = null, supplierInventory = null;
                            try {
                              const [pricesRes, invRes] = await Promise.all([
                                authFetch(`/variants/${v.id}/supplier-prices`),
                                authFetch(`/variants/${v.id}/supplier-inventory`),
                              ]);
                              supplierPrices    = (await pricesRes.json()).rows || [];
                              supplierInventory = (await invRes.json()).rows || [];
                            } catch(_) {}
                            const { fifoIls, fifoUsd } = fifoFromInventory(supplierInventory);
                            setComp(c._k, { variant: v, unit_cost_ils: fifoIls, unit_cost_usd: fifoUsd, allocationValue: null, supplierPrices, supplierInventory });
                          }}
                          getOptionLabel={o=>o?.label??''} isOptionEqualToValue={(a,b)=>a.id===b.id}
                          renderInput={p => <TextField {...p} placeholder="חפש רכיב..." sx={inputSx} />} />
                        {c.variant && <Typography fontSize={10} color="text.secondary">מלאי: {fmtQty(c.variant.quantity??0, c.variant.unit)}</Typography>}
                      </>
                    ) : (
                      <Typography fontSize={13} fontWeight={600}>{c.variant?.label || `#${c._id}`}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isDraft
                      ? <TextField size="small" type="number" value={c.quantity} onChange={e=>setComp(c._k,{quantity:e.target.value})} inputProps={{min:1,step:1,style:{textAlign:'center'}}} sx={{...inputSx,width:80}} />
                      : <Typography fontWeight={600}>{fmtQty(c.quantity, c.variant?.unit)}</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    {isDraft ? (
                      <>
                        <Tooltip title={costTooltip} arrow placement="top">
                          <TextField size="small" type="number" value={c.unit_cost_ils}
                            onChange={e=>setComp(c._k,{unit_cost_ils:e.target.value})}
                            inputProps={{min:0,step:'0.01',style:{textAlign:'center'}}}
                            sx={{...inputSx, width:100,
                              '& .MuiInputBase-root': hasCost
                                ? { backgroundColor: 'rgba(21,87,36,0.07)' }
                                : noCostInv ? { backgroundColor: 'rgba(133,100,4,0.07)' } : {}}} />
                        </Tooltip>
                        {noCostInv && <Typography fontSize={9} color="warning.main" mt={0.3}>הזן ידנית</Typography>}
                      </>
                    ) : (
                      <Typography fontWeight={600} color={NAVY}>₪{Number(c.unit_cost_ils||0).toLocaleString()}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isDraft
                      ? <TextField size="small" type="number" value={c.unit_cost_usd} onChange={e=>setComp(c._k,{unit_cost_usd:e.target.value})} inputProps={{min:0,step:'0.01',style:{textAlign:'center'}}} sx={{...inputSx,width:100}} />
                      : <Typography>${Number(c.unit_cost_usd||0).toLocaleString()}</Typography>}
                  </TableCell>
                  {isDraft && (
                    <TableCell>
                      <IconButton size="small" onClick={()=>setComponents(cs=>cs.filter(x=>x._k!==c._k))} aria-label="הסרת רכיב" sx={{color:'#c62828'}}><DeleteIcon fontSize="small"/></IconButton>
                    </TableCell>
                  )}
                </TableRow>
                {c.variant && (
                  <TableRow>
                    <TableCell colSpan={isDraft ? 5 : 4} sx={{ py: 0, px: 0, border: 'none', borderRight: `3px solid ${GOLD}`, backgroundColor: 'rgba(13,29,62,0.025)' }}>
                      <Box px={2} pt={0.5} pb={1.5}>
                        {isDraft ? (
                          <SupplierAllocationSelector
                            variantId={c.variant.id}
                            quantity={Number(c.quantity) || 1}
                            unit={c.variant.unit}
                            ignoreReservations
                            value={c.allocationValue}
                            onChange={v => {
                              const patch = { allocationValue: v };
                              if (v?.mode === 'supplier_specific') {
                                Object.assign(patch, supplierCostPatch(v.allocations, c.supplierInventory));
                              } else {
                                const { fifoIls, fifoUsd } = fifoFromInventory(c.supplierInventory);
                                if (fifoIls) patch.unit_cost_ils = fifoIls;
                                if (fifoUsd) patch.unit_cost_usd = fifoUsd;
                              }
                              setComp(c._k, patch);
                            }}
                          />
                        ) : (
                          <ComponentFulfillmentView fulfillment={c.fulfillment} unit={c.variant.unit} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
                );
              })}
            </TableBody>
          </Table></Box>
        </CardContent>
      </Card>

      {/* ── Work costs ── */}
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: 3 }}>
          <Stack className="crm-header" direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight={700} color={NAVY}>עלויות עבודה</Typography>
            {isDraft && <Button size="small" startIcon={<AddIcon />} onClick={()=>setCosts(cs=>[...cs,emptyCost()])} sx={btn.primary}>הוסף</Button>}
          </Stack>

          {/* Defined rates panel */}
          {isDraft && (() => {
            const catData = productRoleCosts[batch?.finished_product_id];
            if (!catData || Object.keys(catData).length === 0) return null;
            return (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(13,29,62,0.04)', borderRadius: 1.5,
                border: '1px solid rgba(13,29,62,0.1)' }}>
                <Typography fontSize={11} fontWeight={700} color={NAVY} mb={1}>תעריפים מוגדרים לקטגוריה זו:</Typography>
                <Stack className="crm-wrap" direction="row" flexWrap="wrap" gap={1}>
                  {Object.entries(catData).map(([role, data]) => (
                    <Chip key={role} size="small"
                      label={`${role}: ₪${Number(data.cost_ils).toLocaleString()}`}
                      sx={{ backgroundColor: '#fff', border: '1px solid rgba(13,29,62,0.2)',
                        fontSize: 11, fontWeight: 600, color: NAVY, cursor: 'pointer' }}
                      onClick={() => setCosts(cs => [...cs, { ...emptyCost(), work_type: role, cost: data.cost_ils, currency: data.currency || 'ILS', useGlobal: true }])}
                    />
                  ))}
                </Stack>
                <Typography fontSize={10} color="text.secondary" mt={0.8}>לחץ על תעריף להוספה מהירה</Typography>
              </Box>
            );
          })()}

          {costs.length === 0
            ? <Typography fontSize={12} color="text.secondary">אין עלויות עבודה</Typography>
            : costs.map(c => {
              const roleId      = c.work_type ? roles.find(r => r.name === c.work_type)?.id : null;
              const roleWorkers = roleId ? workers.filter(w => w.role_ids?.includes(roleId)) : workers;
              return isDraft ? (
              <Stack className="crm-wrap" key={c._k} direction="row" spacing={1} mb={1} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel sx={{ fontSize: 12 }}>סוג עבודה</InputLabel>
                  <Select value={c.work_type} label="סוג עבודה" sx={{...inputSx, fontSize: 12}}
                    onChange={e => {
                      const roleName = e.target.value;
                      const roleDef  = roles.find(r => r.name === roleName);
                      const patch = { work_type: roleName };
                      // Clear worker if it doesn't belong to the newly selected role
                      if (c.worker && roleDef) {
                        const wInState = workers.find(w => w.id === c.worker.id);
                        if (!wInState?.role_ids?.includes(roleDef.id)) patch.worker = null;
                      }
                      if (roleDef && c.useGlobal !== false) {
                        patch.cost     = roleDef.cost_currency === 'USD' ? roleDef.default_cost_usd : roleDef.default_cost_ils;
                        patch.currency = roleDef.cost_currency || 'ILS';
                      }
                      setCost(c._k, patch);
                    }}>
                    <MenuItem value=""><em>בחר...</em></MenuItem>
                    {roles.map(r => (
                      <MenuItem key={r.name} value={r.name} sx={{ fontSize: 12 }}>
                        <Stack className="crm-header" direction="row" justifyContent="space-between" width="100%" spacing={1}>
                          <span>{r.name}</span>
                          {(Number(r.default_cost_ils) > 0 || Number(r.default_cost_usd) > 0) && (
                            <Typography fontSize={10} color="text.secondary">
                              {r.cost_currency === 'USD' ? `$${r.default_cost_usd}` : `₪${r.default_cost_ils}`}
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete size="small" options={roleWorkers} value={c.worker} onChange={(_,v)=>setCost(c._k,{worker:v})}
                  getOptionLabel={o=>o?.label??''} isOptionEqualToValue={(a,b)=>a.id===b.id} sx={{width:150}}
                  noOptionsText={c.work_type ? 'אין עובדים לתפקיד זה' : 'בחר סוג עבודה תחילה'}
                  renderInput={p=><TextField {...p} label="עובד" sx={inputSx}/>} />
                {c.useGlobal !== false ? (
                  <Stack alignItems="center" spacing={0.3}>
                    <Typography fontSize={13} fontWeight={700} color={NAVY}>
                      {c.currency==='USD'?'$':'₪'}{Number(c.cost||0).toLocaleString()}
                    </Typography>
                    <Button size="small" variant="text" onClick={()=>setCost(c._k,{useGlobal:false})}
                      sx={{fontSize:10,color:'#856404',p:0,minWidth:0,textDecoration:'underline'}}>ערוך ידנית</Button>
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={0.3}>
                    <TextField size="small" label="עלות" type="number" value={c.cost} onChange={e=>setCost(c._k,{cost:e.target.value})} sx={{...inputSx,width:95}} />
                    <Button size="small" variant="text"
                      onClick={()=>{const rd=roles.find(r=>r.name===c.work_type);setCost(c._k,{useGlobal:true,cost:rd?(rd.cost_currency==='USD'?rd.default_cost_usd:rd.default_cost_ils):c.cost,currency:rd?.cost_currency||c.currency});}}
                      sx={{fontSize:10,color:'#155724',p:0,minWidth:0,textDecoration:'underline'}}>השתמש בגלובלי</Button>
                  </Stack>
                )}
                {c.useGlobal===false && (
                  <FormControl size="small" sx={{width:80}}>
                    <Select value={c.currency} onChange={e=>setCost(c._k,{currency:e.target.value})} sx={inputSx}>
                      {CURRENCY.map(cu=><MenuItem key={cu.id} value={cu.id}>{cu.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <IconButton size="small" onClick={()=>setCosts(cs=>cs.filter(x=>x._k!==c._k))} aria-label="הסרת עלות" sx={{color:'#c62828'}}><DeleteIcon fontSize="small"/></IconButton>
              </Stack>
            ) : (
              <Stack key={c._k} direction="row" spacing={2} mb={0.5}>
                <Typography fontSize={13} fontWeight={600}>{c.work_type || '—'}</Typography>
                <Typography fontSize={13} color="text.secondary">{c.worker?.label}</Typography>
                <Typography fontSize={13} fontWeight={700} color={NAVY}>{c.currency==='USD'?'$':'₪'}{Number(c.cost).toLocaleString()}</Typography>
              </Stack>
            );
          })
          }
        </CardContent>
      </Card>

      {/* ── Cost summary ── */}
      {isDraft && (
        <Card sx={{ ...card.base, mb: 3, backgroundColor: '#f8f5f0', border: `1px solid ${GOLD}` }}>
          <CardContent className="crm-card-content" sx={{ p: 2.5 }}>
            <Typography fontWeight={700} color={NAVY} mb={1.5}>סיכום עלויות (₪)</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 2 }}>
              <Box><Typography fontSize={12} color="text.secondary">עלות קנייה - רכיבים</Typography><Typography fontWeight={700}>₪{totalCompIls.toLocaleString('he-IL',{minimumFractionDigits:0})}</Typography></Box>
              <Box><Typography fontSize={12} color="text.secondary">עלות קנייה - עבודה (₪{unitWorkIls.toLocaleString('he-IL',{minimumFractionDigits:0})}/יח' × {quantity})</Typography><Typography fontWeight={700}>₪{totalWorkIls.toLocaleString('he-IL',{minimumFractionDigits:0})}</Typography></Box>
              <Box><Typography fontSize={12} color="text.secondary">סה"כ עלות קנייה</Typography><Typography fontWeight={700} fontSize={16} color={NAVY}>₪{totalCostIls.toLocaleString('he-IL',{minimumFractionDigits:0})}</Typography></Box>
              <Box><Typography fontSize={12} color="text.secondary">עלות קנייה ליחידה</Typography><Typography fontWeight={800} fontSize={16} color={NAVY}>₪{unitCostIls.toLocaleString('he-IL',{minimumFractionDigits:2})}</Typography></Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Actions ── */}
      {isDraft && (
        <Stack className="crm-actions" direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => navigate(-1)} sx={btn.cancel}>ביטול</Button>
          <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave} disabled={saving || confirming}
            sx={{ borderColor: NAVY, color: NAVY, borderRadius: 2 }}>
            שמור שינויים
          </Button>
          <Button variant="contained"
            startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            onClick={handleConfirm} disabled={saving || confirming}
            sx={{ ...btn.primaryLg, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}>
            אשר ייצור
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default function ProductionShow() {
  return (
    <Show resource="production_batches" title=" " actions={false}>
      <ProductionShowContent />
    </Show>
  );
}
